/**
 * Таблица позиций бюджетного справочника.
 *
 * Колонки по макету v2-01 с правками founder 15.08: «Норма по умолчанию»
 * убрана, колонка называется «Системный справочник», статус сопоставления —
 * значком рядом с наименованием.
 *
 * Плановая цена правится прямо в строке (один клик), остальное — в карточке
 * позиции. Цены хозяйств идут подстроками под позицией ТМЦ: экономист видит
 * исключения, не открывая ничего.
 */
import { useEffect, useRef, useState } from 'react';
import { ChevronRight, Pencil, Plus, RotateCcw, Trash2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  MATCH_BADGE,
  formatFuelRate,
  formatMoneyCell,
  formatMoneyNumber,
  formatPrice,
  isFarmLevel,
  parsePriceInput,
  systemCatalogCellText,
  useItemBookNorms,
  useMeasurementUnits,
  type BudgetBookRef,
  type BudgetItem,
  type BudgetSection,
} from '@/lib/planning/budgetCatalog';
import { cn } from '@/lib/utils';

const rawPriceValue = (v: number | null) => (v === null ? '' : String(v));

function PriceCell({
  item,
  listUpdatedAt,
  canWrite,
  onCommit,
}: {
  item: BudgetItem;
  /**
   * Момент последней перечитки списка, из которого пришёл `item`
   * (`dataUpdatedAt` запроса). Обновляется на КАЖДЫЙ успешный ответ, даже
   * если данные побайтово те же и `data` осталась той же ссылкой.
   */
  listUpdatedAt: number;
  canWrite: boolean;
  /** Реджект = не сохранилось: возвращаем в поле серверное значение. */
  onCommit: (value: number | null) => Promise<unknown>;
}) {
  // Два режима отображения (UAT founder 20.08: «156109.086 голым числом»):
  // не в фокусе — форматированное «156 109,09» (formatMoneyNumber), в фокусе —
  // сырые цифры для набора. Свернули бы к одному виду всегда — либо цена в
  // таблице читалась бы голым числом, либо ввод стал бы неудобным (курсор
  // прыгает вокруг разделителей тысяч на каждый символ).
  const inputRef = useRef<HTMLInputElement>(null);
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(rawPriceValue(item.planned_price));
  const [invalid, setInvalid] = useState(false);
  // «То, что мы только что сохранили» — держит цену без мигания СТАРЫМ
  // значением между ответом сервера и перечиткой списка (head-web ревью
  // #850, B2: `usePatchBudgetItem` не делает оптимистичный апдейт кэша,
  // `invalidate()` только СТАВИТ список устаревшим — сам рефетч приезжает
  // отдельным сетевым запросом позже). Без этого поля набрал «1400» → блюр →
  // на миг «1 350,00» (старое, из ещё не перечитанного `item.planned_price`)
  // → потом «1 400,00» — ровно та «усталость», которую чинили.
  //
  // `display` может САМ быть `null` (пользователь очистил цену) — потому
  // оверрайд завёрнут в объект, а не голое `number | null`: иначе «очищено
  // локально» неотличимо от «оверрайда нет вовсе», и очистка цены повторяла
  // бы то же самое мигание (head-web ревью #850, follow-up).
  //
  // У оверрайда есть СРОК ЖИЗНИ — до следующей перечитки списка (AK-707,
  // head-web ревью #850): `listAt` — момент перечитки на момент отправки.
  // Без него оверрайд снимался только когда проп УШЁЛ от значения «до
  // сохранения»; если PATCH ответил 200, а перечитка принесла ровно прежнее
  // число (принят, но не сохранён; сосед откатил параллельно), «1 400,00»
  // висело до размонтирования — человек читал несохранённое как сохранённое.
  const [justSaved, setJustSaved] = useState<{
    display: number | null;
    from: number | null;
    listAt: number;
  } | null>(null);

  useEffect(() => {
    if (!editing) setValue(rawPriceValue(item.planned_price));
    // Снимаем оверрайд ПО ФАКТУ обновления списка: либо проп ушёл от
    // значения, которое было ДО сохранения, либо список перечитан после
    // отправки (`listUpdatedAt` вырос) — что бы он ни принёс, это правда
    // сервера, а оверрайд был лишь мостиком до неё. Не по точному равенству
    // с тем, что отправили: оно залипло бы навсегда, если сервер вернёт
    // ДРУГОЕ число — параллельная правка, а с приходом AK-704 (квантование
    // цен до сотых) это норма: прислали 156109.086, бэк ответил 156109.09.
    if (justSaved && (item.planned_price !== justSaved.from || listUpdatedAt > justSaved.listAt)) {
      setJustSaved(null);
    }
  }, [item.planned_price, listUpdatedAt, editing, justSaved]);

  if (!canWrite || item.archived_at) {
    return <span className="tabular-nums">{formatPrice(item.planned_price)}</span>;
  }

  const displayPrice = justSaved ? justSaved.display : item.planned_price;
  const displayValue = editing ? value : formatMoneyNumber(displayPrice);

  return (
    <Input
      ref={inputRef}
      value={displayValue}
      inputMode="decimal"
      aria-label={`Плановая цена — ${item.name}`}
      aria-invalid={invalid || undefined}
      className={cn('h-8 w-28 text-right tabular-nums', invalid && 'border-destructive')}
      onFocus={() => {
        setEditing(true);
        setJustSaved(null);
        setValue(rawPriceValue(item.planned_price));
      }}
      onChange={(e) => setValue(e.target.value)}
      onBlur={() => {
        const parsed = parsePriceInput(value);
        if (parsed === undefined) {
          // Мусор остаётся в поле как есть (не форматируем, не откатываем) —
          // человек должен видеть, что именно он набрал, и поправить это.
          setInvalid(true);
          return;
        }
        setInvalid(false);
        if (parsed === item.planned_price) {
          setEditing(false);
          return;
        }
        // Значение ДО сохранения и момент перечитки списка НА МОМЕНТ отправки —
        // по ним следующий эффект поймёт, что список реально перечитался
        // (а не просто случайно совпал). Версию берём здесь, в блюре, а не в
        // `.then`: перечитка, завершившаяся ПОКА летел PATCH, тоже новее
        // нашего ввода — и тоже правда сервера, которой оверрайд уступает.
        const savedFrom = item.planned_price;
        const savedListAt = listUpdatedAt;
        // ОСТАЁМСЯ в режиме редактирования (сырое «1400» на экране) до
        // ответа сервера — переключаемся на форматированный вид ТОЛЬКО
        // когда есть что показать без мигания: успех — сохранённое значение
        // сразу (`justSaved`), отказ — прежнее серверное.
        void onCommit(parsed)
          .then(() => {
            setJustSaved({ display: parsed, from: savedFrom, listAt: savedListAt });
            // Пользователь мог успеть вернуться в поле и набирать заново,
            // пока ответ на ПРЕЖНИЙ PATCH ещё летел — ответ по старому
            // вводу не должен выбивать его из уже нового редактирования.
            if (document.activeElement !== inputRef.current) setEditing(false);
          })
          .catch(() => {
            // Отказ бэка (409/422/403) НЕ должен оставлять набранную цифру в
            // поле — иначе пользователь читает несохранённое как сохранённое.
            setValue(rawPriceValue(item.planned_price));
            if (document.activeElement !== inputRef.current) setEditing(false);
          });
      }}
    />
  );
}

/**
 * ГЛАВНАЯ цифра расценки в режиме книги — ДИАПАЗОН по агрегатам.
 *
 * Медиана отсюда убрана насовсем (founder 26.08: «какая может быть медиана?»).
 * В книге ЕТС нет цены работы — есть цена работы конкретным агрегатом, и
 * разброс внутри одной работы доходит до 5x. Одно число на её месте — выдумка.
 *
 * Один агрегат — печатаем его точную цену, а не «151,00 – 151,00»: диапазон из
 * одного значения читается как ошибка вёрстки.
 */
export function priceRangeLabel(book: BudgetBookRef | null | undefined): string | null {
  if (!book || book.pay_min === null || book.pay_max === null) return null;
  if (book.pay_min >= book.pay_max) return `${formatMoneyNumber(book.pay_min)} ₽`;
  return `${formatMoneyNumber(book.pay_min)} – ${formatMoneyNumber(book.pay_max)} ₽`;
}

/**
 * Раскрытие строки: нормативы книги ЕТС по агрегатам — «тягач · орудие ·
 * ширина · норма за смену · расценка · топливо», как в исходном файле
 * финансистов. Read-only: правда здесь принадлежит книге, правится позиция,
 * а не строка книги.
 */
function BookNormsPanel({
  section,
  itemId,
  unit,
}: {
  section: BudgetSection;
  itemId: string;
  unit: string;
}) {
  const norms = useItemBookNorms(section, itemId);

  if (norms.isPending) {
    return <div className="p-4 text-sm text-muted-foreground">Читаем книгу нормативов…</div>;
  }
  if (norms.isError) {
    return (
      <div className="p-4 text-sm text-destructive">
        Не удалось прочитать нормативы: {norms.error.message}
      </div>
    );
  }
  if (!norms.data?.norms.length) {
    return (
      <div className="p-4 text-sm text-muted-foreground">
        За этой позицией строк книги нет — расценка заведена руками.
      </div>
    );
  }

  return (
    <div className="p-4">
      {norms.data.count_changed && (
        <p className="mb-2 text-sm text-muted-foreground">
          Состав изменился: сейчас в книге {norms.data.norms.length} строк, при наполнении было{' '}
          {norms.data.snapshot_count}. Число агрегатов в таблице — снимок наполнения.
        </p>
      )}
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Трактор / комбайн</TableHead>
            <TableHead>С/х машина</TableHead>
            <TableHead className="w-[10%] text-right">Ширина, м</TableHead>
            <TableHead className="w-[12%] text-right">Норма за смену</TableHead>
            <TableHead className="w-[14%] text-right">Расценка, ₽/{unit}</TableHead>
            <TableHead className="w-[12%] text-right">Топливо, л/{unit}</TableHead>
            <TableHead className="w-[12%]">Строка книги</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {norms.data.norms.map((n) => (
            <TableRow key={n.id}>
              <TableCell>
                {n.prime_mover ?? n.executor_hint_raw ?? '—'}
                {n.machines_ambiguous && (
                  <Badge
                    variant="outline"
                    className="ml-2"
                    title="в книге перечислено несколько марок — взаимозаменяемые они или состав сцепки, источник не говорит"
                  >
                    марка не однозначна
                  </Badge>
                )}
              </TableCell>
              <TableCell>
                {n.implement ?? '—'}
                {n.implement_count && n.implement_count > 1 ? ` × ${n.implement_count}` : ''}
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {n.width_m ?? '—'}
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {n.norm_per_shift ?? '—'}
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {formatMoneyCell(n.pay_per_unit)}
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {formatFuelRate(n.fuel_rate)}
              </TableCell>
              <TableCell className="text-muted-foreground">
                {n.source_sheet ?? '—'}
                {n.source_row ? `, строка ${n.source_row}` : ''}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export function BudgetItemsTable({
  rows,
  section,
  listUpdatedAt,
  priceScopeLabel,
  templateReadOnly,
  canWrite,
  canCreate,
  canArchive,
  onEdit,
  onArchive,
  onPriceCommit,
  onAdoptTemplate,
  onAddFarmPrice,
  onEditFarmPrice,
  onDeleteFarmPrice,
}: {
  rows: BudgetItem[];
  section: BudgetSection;
  /** Момент последней перечитки списка `rows` (`dataUpdatedAt` запроса) — срок жизни оверрайда цены в ячейке. */
  listUpdatedAt: number;
  /** Подпись под заголовком цены: чья это цена — компании, шаблона, хозяйства. */
  priceScopeLabel: string;
  /**
   * Выбрано конкретное хозяйство в farm-разрезном разделе. Тогда строки без
   * хозяйства — ЧУЖИЕ (шаблон компании): правка отсюда меняла бы цифры всем
   * хозяйствам сразу. Их показываем только для чтения, с действием
   * «Взять в хозяйство» (порция 3 founder: компания — шаблон, не место правды).
   */
  templateReadOnly: boolean;
  canWrite: boolean;
  canCreate: boolean;
  canArchive: boolean;
  onEdit: (item: BudgetItem) => void;
  onArchive: (item: BudgetItem) => void;
  onPriceCommit: (item: BudgetItem, value: number | null) => Promise<unknown>;
  onAdoptTemplate: (item: BudgetItem) => void;
  onAddFarmPrice: (item: BudgetItem) => void;
  onEditFarmPrice: (item: BudgetItem, farmId: string) => void;
  onDeleteFarmPrice: (item: BudgetItem, farmId: string) => void;
}) {
  const units = useMeasurementUnits();
  const unitSymbol = (unitId: string | null) =>
    units.data?.find((u) => u.id === unitId)?.symbol ?? '—';

  const farmLevel = isFarmLevel(section);
  // Расценки работ показываются В ФОРМАТЕ КНИГИ ЕТС, а не общим прайсом
  // «название — цена» (претензия founder 26.08: «на Excel не похоже»). Отсюда
  // три отличия от остальных разделов:
  //  • параметры операции — ОТДЕЛЬНОЙ колонкой. В имени они слеплены
  //    («Пахота, глубина 20-22 см») и глазами со столбцом книги не сверяются;
  //  • «Тип» и «Системный справочник» скрыты: у расценок системного каталога
  //    нет по определению (NO_SYSTEM_CATALOG), а `item_kind` в книге не
  //    существует — обе колонки стоят пустыми и мешают читать;
  //  • строка раскрывается в нормативы книги по агрегатам.
  const bookFormat = section === 'work_rate';
  const showKind = section !== 'sale_product' && !bookFormat;
  const showGrade = section === 'sale_product';
  const showSystemRef = !bookFormat;
  const [expanded, setExpanded] = useState<string | null>(null);
  // Подстрока цены хозяйства схлопывает колонки до «Ед.».
  const leadingCols =
    (bookFormat ? 2 : 0) + (showKind ? 1 : 0) + 1 + (showGrade ? 1 : 0) + (showSystemRef ? 1 : 0);
  // Ед. + расценка + [топливо] + [агрегатов] + источник + действия.
  const totalCols = leadingCols + (bookFormat ? 4 : 2) + 2;

  return (
    <div className="overflow-hidden rounded-lg border border-border">
      <Table>
        <TableHeader>
          <TableRow>
            {bookFormat && <TableHead className="w-8" />}
            {showKind && <TableHead className="w-[12%]">Тип</TableHead>}
            <TableHead>{bookFormat ? 'Работа' : 'Наименование в бюджете'}</TableHead>
            {bookFormat && <TableHead className="w-[18%]">Параметры</TableHead>}
            {showGrade && <TableHead className="w-[14%]">Класс качества</TableHead>}
            {showSystemRef && <TableHead className="w-[15%]">Системный справочник</TableHead>}
            <TableHead className="w-[8%]">Ед.</TableHead>
            <TableHead className="w-[14%] text-right">
              {bookFormat ? 'Расценка' : 'Плановая цена'}
              <span className="block text-xs font-normal text-muted-foreground">
                {bookFormat ? 'диапазон по агрегатам' : priceScopeLabel}
              </span>
            </TableHead>
            {bookFormat && (
              <TableHead className="w-[10%] text-right">
                Топливо
                <span className="block text-xs font-normal text-muted-foreground">
                  литров на ед.
                </span>
              </TableHead>
            )}
            {bookFormat && (
              <TableHead className="w-[9%] text-right">
                Агрегатов
                <span className="block text-xs font-normal text-muted-foreground">
                  строк книги
                </span>
              </TableHead>
            )}
            <TableHead className="w-[13%]">{bookFormat ? 'Источник' : 'Источник цены'}</TableHead>
            <TableHead className="w-[18%]" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((item) => {
            const badge = MATCH_BADGE[item.match_status] ?? MATCH_BADGE.queued;
            const archived = !!item.archived_at;
            // Строка шаблона компании, открытая из разреза хозяйства.
            const foreignTemplate = templateReadOnly && item.farm_id === null;
            const editable = canWrite && !archived && !foreignTemplate;
            return [
              <TableRow key={item.id} className={cn(archived && 'text-muted-foreground')}>
                {bookFormat && (
                  <TableCell className="p-0 align-middle">
                    {item.book ? (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        aria-expanded={expanded === item.id}
                        aria-label={
                          expanded === item.id
                            ? `Свернуть агрегаты — ${item.name}`
                            : `Показать агрегаты — ${item.name}`
                        }
                        onClick={() => setExpanded((cur) => (cur === item.id ? null : item.id))}
                      >
                        <ChevronRight
                          className={cn(
                            'h-4 w-4 transition-transform',
                            expanded === item.id && 'rotate-90',
                          )}
                        />
                      </Button>
                    ) : null}
                  </TableCell>
                )}
                {showKind && <TableCell>{item.item_kind ?? '—'}</TableCell>}
                <TableCell>
                  <span className={cn('font-medium', archived && 'line-through')}>
                    {bookFormat ? (item.tenant_item_name ?? item.name) : item.name}
                  </span>{' '}
                  {bookFormat && item.book?.work_kind === 'transport' && (
                    <Badge variant="outline" title="строка транспортного раздела книги">
                      транспортные
                    </Badge>
                  )}{' '}
                  {/* Значок сопоставления в расценках работ ВРЁТ: его подпись
                      обещает «сопоставлено с системным справочником», а
                      системного справочника у расценок нет по определению
                      (NO_SYSTEM_CATALOG). Галочка стоит лишь потому, что
                      позиция связана с типом операции хозяйства. */}
                  {!bookFormat && (
                    <Badge variant={badge.variant} title={badge.title}>
                      {badge.label}
                    </Badge>
                  )}
                  {farmLevel && templateReadOnly && item.farm_id === null && (
                    <Badge variant="outline" className="ml-2" title="цифра шаблона компании — правится в режиме «Шаблон компании»">
                      из шаблона
                    </Badge>
                  )}
                </TableCell>
                {bookFormat && (
                  <TableCell className="text-muted-foreground">
                    {item.book?.params ?? '—'}
                  </TableCell>
                )}
                {showGrade && <TableCell>{item.grade ?? '—'}</TableCell>}
                {showSystemRef && (
                  <TableCell className="text-muted-foreground">
                    {systemCatalogCellText(item)}
                  </TableCell>
                )}
                <TableCell>{unitSymbol(item.unit_id)}</TableCell>
                <TableCell className="text-right">
                  {bookFormat && item.book ? (
                    <div className="space-y-1">
                      {/* Диапазон — ГЛАВНАЯ цифра, не приглушённая подпись под
                          ценой. Цены у работы нет: она есть у агрегата.
                          Условие на `item.book`, а не только на раздел: у
                          позиции, заведённой руками, книги нет, и прочерк
                          главной строкой НАД её живой ценой читался бы как
                          «цены нет» — прямо над этой самой ценой. */}
                      <span className="block font-medium tabular-nums">
                        {priceRangeLabel(item.book) ?? '—'}
                      </span>
                      {/* Книга = кластер: диапазон по двум книгам это диапазон
                          по двум регионам, и такой цифры может не быть ни в
                          одном из них. Подписываем, а не выдаём за одну. */}
                      {(item.book?.books_count ?? 0) > 1 && (
                        <span
                          className="block text-xs text-muted-foreground"
                          title="расценки собраны из нескольких книг — по регионам они разные; смотрите дерево с выбранной книгой"
                        >
                          по {item.book?.books_count} книгам
                        </span>
                      )}
                      {(editable || item.planned_price !== null) && (
                        <div className="flex flex-col items-end gap-1">
                          <PriceCell
                            item={item}
                            listUpdatedAt={listUpdatedAt}
                            canWrite={editable}
                            onCommit={(value) => onPriceCommit(item, value)}
                          />
                          {item.planned_price !== null && (
                            <span
                              className="text-xs text-muted-foreground"
                              title="цену вписал человек; заливка и ресинк её не трогают"
                            >
                              задана вручную
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <PriceCell
                      item={item}
                      listUpdatedAt={listUpdatedAt}
                      canWrite={editable}
                      onCommit={(value) => onPriceCommit(item, value)}
                    />
                  )}
                </TableCell>
                {bookFormat && (
                  <TableCell className="text-right tabular-nums text-muted-foreground">
                    {formatFuelRate(item.book?.fuel_rate)}
                  </TableCell>
                )}
                {bookFormat && (
                  <TableCell className="text-right tabular-nums text-muted-foreground">
                    {item.book?.norms_count ?? '—'}
                  </TableCell>
                )}
                <TableCell className="text-muted-foreground">
                  {archived ? 'убрана из бюджета' : (item.price_source ?? '—')}
                </TableCell>
                <TableCell>
                  <div className="flex flex-nowrap items-center justify-end gap-1">
                    {foreignTemplate && canCreate && !archived && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="whitespace-nowrap px-2 text-xs"
                        onClick={() => onAdoptTemplate(item)}
                        title="Скопировать строку шаблона в это хозяйство и править её здесь"
                      >
                        Взять в хозяйство
                      </Button>
                    )}
                    {editable && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onEdit(item)}
                        aria-label={`Изменить позицию ${item.name}`}
                        title="Изменить позицию"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                    )}
                    {editable && !farmLevel && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="whitespace-nowrap px-2 text-xs"
                        onClick={() => onAddFarmPrice(item)}
                        aria-label={`Цена для хозяйства — ${item.name}`}
                      >
                        <Plus className="mr-1 h-4 w-4" />
                        Цена хозяйства
                      </Button>
                    )}
                    {archived && canWrite && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="whitespace-nowrap px-2 text-xs"
                        onClick={() => onArchive(item)}
                      >
                        <RotateCcw className="mr-1 h-4 w-4" />
                        Вернуть
                      </Button>
                    )}
                    {!archived && canArchive && !foreignTemplate && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="whitespace-nowrap px-2 text-xs"
                        onClick={() => onArchive(item)}
                        aria-label={`Убрать из бюджета — ${item.name}`}
                        title="Позиция останется в справочнике хозяйства и в факте прошлого сезона"
                      >
                        Убрать
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>,
              ...(bookFormat && expanded === item.id
                ? [
                    <TableRow key={`${item.id}-norms`} className="bg-muted/50 hover:bg-muted/50">
                      <TableCell colSpan={totalCols} className="p-0">
                        <BookNormsPanel
                          section={section}
                          itemId={item.id}
                          unit={unitSymbol(item.unit_id)}
                        />
                      </TableCell>
                    </TableRow>,
                  ]
                : []),
              ...item.farm_prices.map((fp) => (
                <TableRow key={`${item.id}-${fp.farm_id}`} className="bg-muted">
                  <TableCell colSpan={leadingCols} className="text-muted-foreground">
                    ↳ цена для хозяйства «{fp.farm_name ?? '—'}»
                  </TableCell>
                  <TableCell>{unitSymbol(fp.price_unit_id ?? item.unit_id)}</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatPrice(fp.price)}
                  </TableCell>
                  {/* Топливо и число агрегатов — свойства РАБОТЫ, а не цены
                      хозяйства. Ячейки пустые, но обязаны быть: без них строка
                      разъезжается с шапкой. */}
                  {bookFormat && <TableCell />}
                  {bookFormat && <TableCell />}
                  <TableCell className="text-muted-foreground">
                    {fp.price_source ?? 'своя цена хозяйства'}
                  </TableCell>
                  <TableCell>
                    {canWrite && (
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onEditFarmPrice(item, fp.farm_id)}
                          aria-label={`Изменить цену хозяйства ${fp.farm_name ?? ''} — ${item.name}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onDeleteFarmPrice(item, fp.farm_id)}
                          aria-label={`Удалить цену хозяйства ${fp.farm_name ?? ''} — ${item.name}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              )),
            ];
          })}
        </TableBody>
      </Table>
    </div>
  );
}
